import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Box, Typography, Grid, Card, CardContent, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, MenuItem, List, ListItem, ListItemText, Divider, CircularProgress, useTheme, useMediaQuery, Chip } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { flatsAPI, buildingsAPI, usersAPI } from '../services/api';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../contexts/AuthContext';

const safeArray = (val) => Array.isArray(val) ? val : [];

const groupIncludes = (groups, groupName) =>
  Array.isArray(groups) && groups.some(g => {
    const name = typeof g === 'string' ? g : g.name;
    return name && name.toLowerCase() === groupName.toLowerCase();
  });
const groupIncludesAny = (groups, groupNames) =>
  groupNames.some(name => groupIncludes(groups, name));

// FlatDetailsDialog Component
const FlatDetailsDialog = ({ open, onClose, flatId, flatNumber }) => {
  const { user, loading: authLoading } = useAuth();
  const [flat, setFlat] = useState(null);
  const [history, setHistory] = useState([]);
  const [owner, setOwner] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  React.useEffect(() => {
    if (!open || !flatId) return;
    
    setLoading(true);
    setError('');
    
    Promise.all([
      flatsAPI.getDetails(flatId, flatNumber),
      flatsAPI.getHistory(flatId)
    ])
      .then(async ([flatRes, historyRes]) => {
        setFlat(flatRes.data);
        console.log('flatRes.data:', flatRes.data);
        console.log('historyRes.data:', historyRes.data);
        setHistory(historyRes.data || []);
        
        // Fetch owner and tenants details
        const ownerId = flatRes.data.currentOwnerId;
        const tenantIds = Array.isArray(flatRes.data.currentTenantIds) ? flatRes.data.currentTenantIds : [];
        let ownerData = null;
        let tenantsData = [];
        
        if (ownerId) {
          try {
            const ownerRes = await usersAPI.getById(ownerId);
            ownerData = ownerRes.data;
          } catch {}
        }
        
        if (tenantIds.length > 0) {
          try {
            const tenantsRes = await Promise.all(tenantIds.map(id => usersAPI.getById(id)));
            tenantsData = tenantsRes.map(r => r.data);
          } catch {}
        }
        
        setOwner(ownerData);
        setTenants(tenantsData);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'Failed to load flat details');
        setLoading(false);
      });
  }, [open, flatId]);

  const handleClose = () => {
    setFlat(null);
    setHistory([]);
    setOwner(null);
    setTenants([]);
    setError('');
    onClose();
  };

  // Access control: only admin, maintenance_staff, current owner, or current tenant
  const groups = user?.groups || [];
  const userId = user?.attributes?.sub || user?.username;
  const isAllowed = flat ? (
    groupIncludesAny(groups, ['admin', 'maintenance_staff']) ||
    (flat.currentOwnerId && flat.currentOwnerId === userId) ||
    (Array.isArray(flat.currentTenantIds) && flat.currentTenantIds.includes(userId))
  ) : false;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Flat Details</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : !flat ? (
          <Alert severity="info">No flat found.</Alert>
        ) : !isAllowed ? (
          <Alert severity="error">You do not have permission to view this flat's details.</Alert>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <Typography><b>Flat Number:</b> {flat.flatNumber}</Typography>
              <Typography><b>Building ID:</b> {flat.buildingId}</Typography>
              <Typography><b>Floor Number:</b> {flat.floorNumber}</Typography>
              <Typography><b>Type:</b> {flat.flatType}</Typography>
              <Typography><b>Area:</b> {flat.area}</Typography>
              <Typography><b>Monthly Maintenance:</b> {flat.monthlyMaintenance}</Typography>
              <Typography><b>Status:</b> {flat.isActive ? 'Active' : 'Inactive'}</Typography>
              <Typography><b>Occupied:</b> {flat.isOccupied ? 'Yes' : 'No'}</Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Current Owner</Typography>
            {owner ? (
              <Typography sx={{ mb: 2 }}>{owner.firstName} {owner.lastName} ({owner.email})</Typography>
            ) : (
              <Typography color="text.secondary" sx={{ mb: 2 }}>No owner info</Typography>
            )}
            
            <Typography variant="h6" gutterBottom>Current Tenants</Typography>
            {tenants.length > 0 ? (
              <List dense>
                {tenants.map(t => (
                  <ListItem key={t.userId}>
                    <ListItemText primary={`${t.firstName} ${t.lastName}`} secondary={t.email} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="text.secondary" sx={{ mb: 2 }}>No tenants</Typography>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Ownership & Tenant History</Typography>
            {history.length === 0 ? (
              <Typography color="text.secondary">No history found.</Typography>
            ) : (
              <List dense>
                {history.map((h, idx) => (
                  <ListItem key={idx}>
                    <ListItemText
                      primary={`${h.role}: ${h.userId}`}
                      secondary={`From: ${h.fromDate} To: ${h.toDate || 'Present'}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const Flats = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedFlatId, setSelectedFlatId] = useState(null);
  const [selectedFlatNumber, setSelectedFlatNumber] = useState(null);
  const [formData, setFormData] = useState({
    flatNumber: '',
    buildingId: '',
    blockId: '',
    flatType: '',
    floorNumber: '',
    area: '',
    monthlyMaintenance: '',
  });
  const [error, setError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const queryClient = useQueryClient();
  const [selectedBuilding, setSelectedBuilding] = useState('');
  /* const { data: buildings = [], isLoading: isBuildingsLoading } = 
  useQuery('buildings', async () => (await buildingsAPI.getAll()).data); */
  const { data: buildings = [], isLoading: isBuildingsLoading } =
   useQuery('buildings', async () => (await buildingsAPI.getAll()).data, {
    onSuccess: (data) => {
      console.log('Buildings data received:', data);
    },
    onError: (error) => {
      console.error('Buildings API error:', error);
    }
  });
  console.log('Buildings data received:', buildings);
  const { data: flats = [], isLoading } = useQuery('flats', async () => (await flatsAPI.getAll()).data);
  const filteredFlats = selectedBuilding ? flats.filter(f => f.buildingId === selectedBuilding) : flats;

  // Add a separate query for dialog
  const { data: dialogBuildings = [], isLoading: isDialogBuildingsLoading } = useQuery(
    'buildings-dialog',
    async () => (await buildingsAPI.getAll()).data,
    { 
      enabled: open,
      onSuccess: (data) => {
        console.log('Dialog buildings data received:', data);
      },
      onError: (error) => {
        console.error('Dialog buildings API error:', error);
      }
    }
  );

  const createMutation = useMutation(flatsAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('flats');
      handleClose();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to create flat');
    },
  });

  // Update mutation
  const updateMutation = useMutation(
    ({ flatId, data }) => flatsAPI.update(flatId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('flats');
        setEditDialogOpen(false);
      },
      onError: (error) => {
        setError(error.response?.data?.message || 'Failed to update flat');
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (flatId) => flatsAPI.delete(flatId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('flats');
      },
      onError: (error) => {
        setDeleteError(error.response?.data?.message || 'Failed to delete flat');
      },
    }
  );

  const handleOpen = () => {
    setFormData({ flatNumber: '', buildingId: '', blockId: '', flatType: '', floorNumber: '', area: '', monthlyMaintenance: '' });
    setError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ flatNumber: '', buildingId: '', blockId: '', flatType: '', floorNumber: '', area: '', monthlyMaintenance: '' });
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleViewDetails = (flatId, flatNumber) => {
    setSelectedFlatId(flatId);
    setSelectedFlatNumber(flatNumber);
    setDetailsDialogOpen(true);
  };

  // Edit dialog open
  const handleEditOpen = (flat) => {
    setEditFormData({ ...flat });
    setError('');
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditFormData(null);
    setError('');
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (editFormData && editFormData.flatId) {
      updateMutation.mutate({ flatId: editFormData.flatId, data: editFormData });
    }
  };

  // Delete flat
  const handleDeleteFlat = (flatId) => {
    if (window.confirm('Are you sure you want to delete this flat?')) {
      deleteMutation.mutate(flatId);
    }
  };

  // Helper to get blocks for a building
  const getBlocksForBuilding = (buildingId) => {
    const building = safeArray(buildings).find(b => b.buildingId === buildingId);
    return building && Array.isArray(building.blocks) ? building.blocks : [];
  };

  if (flats && flats.message) {
    return <Typography color="error">{flats.message}</Typography>;
  }
  const flatsSafe = safeArray(flats);
  const buildingsSafe = safeArray(buildings);
  const dialogBuildingsSafe = safeArray(dialogBuildings);

  const columns = [
    { field: 'flatNumber', headerName: 'Flat Number', width: 130 },
    { field: 'blockId', headerName: 'Block', width: 120, 
      valueGetter: (params) => {
        const building = buildingsSafe.find(b => b.buildingId === params.row.buildingId);
        const block = (building?.blocks || []).find(bl => bl.blockId === params.row.blockId);
        return block ? block.blockName || block.blockId : params.row.blockId || '';
      }
    },
    { field: 'flatType', headerName: 'Flat Type', width: 130 },
    { field: 'floorNumber', headerName: 'Floor Number', width: 130 },
    { field: 'area', headerName: 'Area (sq ft)', width: 130 },
    { field: 'monthlyMaintenance', headerName: 'Monthly Maintenance', width: 130 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 350, // Increased width for all buttons to be visible
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleViewDetails(params.row.flatId, params.row.flatNumber)}
          >
            View Details
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            onClick={() => handleEditOpen(params.row)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<Delete />}
            onClick={() => handleDeleteFlat(params.row.flatId)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
      }}>
        <Typography variant={isMobile ? "h5" : "h4"}>Flats Management</Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={handleOpen}
          fullWidth={isMobile}
          sx={{ minHeight: isMobile ? 44 : 'auto' }}
        >
          Add Flat
        </Button>
      </Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          select
          label="Select Building"
          value={selectedBuilding}
          onChange={e => setSelectedBuilding(e.target.value)}
          disabled={isBuildingsLoading}
          sx={{ 
            minWidth: isMobile ? '100%' : 250,
            width: isMobile ? '100%' : 'auto'
          }}
        >
          <MenuItem value="">All Buildings</MenuItem>
          {Array.isArray(buildingsSafe) && buildingsSafe.map(b => (
            <MenuItem key={b.buildingId} value={b.buildingId}>
              {b.buildingName || b.buildingId}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : filteredFlats.length === 0 ? (
        <Typography>No flats found for this building.</Typography>
      ) : (
        <>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredFlats.map((flat) => {
                const building = buildingsSafe.find(b => b.buildingId === flat.buildingId);
                const block = (building?.blocks || []).find(bl => bl.blockId === flat.blockId);
                const blockName = block ? block.blockName || block.blockId : flat.blockId || '';
                
                return (
                  <Card key={flat.flatId} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Flat {flat.flatNumber}
                      </Typography>
                      <Chip label={flat.flatType} color="primary" size="small" />
                    </Box>
                    
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Block:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{blockName}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Floor:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{flat.floorNumber}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Area:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{flat.area} sq ft</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Maintenance:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>₹{flat.monthlyMaintenance}</Typography>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleViewDetails(flat.flatId, flat.flatNumber)}
                        fullWidth
                        sx={{ minHeight: 44 }}
                      >
                        View Details
                      </Button>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => handleEditOpen(flat)}
                          sx={{ flex: 1, minHeight: 44 }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<Delete />}
                          onClick={() => handleDeleteFlat(flat.flatId)}
                          sx={{ flex: 1, minHeight: 44 }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <DataGrid
              rows={filteredFlats}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              loading={isLoading}
              getRowId={(row) => row.flatId}
            />
          )}
        </>
      )}
      
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ position: isMobile ? 'sticky' : 'static', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          Add New Flat
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pb: isMobile ? 2 : 1 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField 
                label="Flat Number" 
                value={formData.flatNumber} 
                onChange={e => setFormData({ ...formData, flatNumber: e.target.value })} 
                required 
                fullWidth 
              />
              <TextField
                select
                label="Building"
                name="buildingId"
                value={formData.buildingId}
                onChange={e => setFormData({ ...formData, buildingId: e.target.value, blockId: '' })}
                required
                fullWidth
                margin="normal"
                disabled={isDialogBuildingsLoading}
              >
                {Array.isArray(buildingsSafe) && buildingsSafe.map(b => (
                  <MenuItem key={b.buildingId} value={b.buildingId}>
                    {b.buildingName || b.buildingId}
                  </MenuItem>
                ))}
              </TextField>
              {/* Block Dropdown */}
              {formData.buildingId && (
                <TextField
                  select
                  label="Block"
                  name="blockId"
                  value={formData.blockId}
                  onChange={e => {
                    const selectedBlockId = e.target.value;
                    const block = getBlocksForBuilding(formData.buildingId).find(b => b.blockId === selectedBlockId);
                    setFormData(prev => ({
                      ...prev,
                      blockId: selectedBlockId,
                      blockName: block ? block.blockName : ''
                    }));
                  }}
                  required
                  fullWidth
                  margin="normal"
                >
                  <MenuItem value="">Select Block</MenuItem>
                  {getBlocksForBuilding(formData.buildingId).map(block => (
                    <MenuItem key={block.blockId} value={block.blockId}>
                      {block.blockName || block.blockId}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                select
                SelectProps={{ native: true }}
                label="Flat Type"
                value={formData.flatType}
                onChange={e => setFormData({ ...formData, flatType: e.target.value })}
                required
                fullWidth
              >
                <option value="">Select Flat Type</option>
                <option value="1">1BHK</option>
                <option value="2">2BHK</option>
                <option value="3">3BHK</option>
                <option value="4">4BHK</option>
                <option value="5">5BHK</option>
              </TextField>
              <TextField
                label="Floor Number"
                type="number"
                value={formData.floorNumber}
                onChange={e => setFormData({ ...formData, floorNumber: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Area (sq ft)"
                type="number"
                value={formData.area}
                onChange={e => setFormData({ ...formData, area: e.target.value })}
                required
                fullWidth
              />
              <TextField 
                label="Monthly Maintenance" 
                type="number" 
                value={formData.monthlyMaintenance} 
                onChange={e => setFormData({ ...formData, monthlyMaintenance: e.target.value })} 
                required 
                fullWidth 
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            position: isMobile ? 'sticky' : 'static', 
            bottom: 0, 
            bgcolor: 'background.paper', 
            p: 2,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0
          }}>
            <Button 
              onClick={handleClose}
              fullWidth={isMobile}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={createMutation.isLoading}
              fullWidth={isMobile}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              {createMutation.isLoading ? 'Adding...' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Flat Details Dialog */}
      <FlatDetailsDialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)} 
        flatId={selectedFlatId}
        flatNumber={selectedFlatNumber}  
      />

      {/* Edit Flat Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleEditClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ position: isMobile ? 'sticky' : 'static', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
          Edit Flat
        </DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent sx={{ pb: isMobile ? 2 : 1 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {editFormData && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  label="Flat Number"
                  value={editFormData.flatNumber}
                  onChange={e => setEditFormData({ ...editFormData, flatNumber: e.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  select
                  label="Building"
                  name="buildingId"
                  value={editFormData.buildingId}
                  onChange={e => setEditFormData({ ...editFormData, buildingId: e.target.value, blockId: '' })}
                  required
                  fullWidth
                  margin="normal"
                  disabled={isDialogBuildingsLoading}
                >
                  {Array.isArray(buildingsSafe) && buildingsSafe.map(b => (
                    <MenuItem key={b.buildingId} value={b.buildingId}>
                      {b.buildingName || b.buildingId}
                    </MenuItem>
                  ))}
                </TextField>
                {/* Block Dropdown */}
                {editFormData.buildingId && (
                  <TextField
                    select
                    label="Block"
                    name="blockId"
                    value={editFormData.blockId}
                    onChange={e => {
                      const selectedBlockId = e.target.value;
                      const block = getBlocksForBuilding(editFormData.buildingId).find(b => b.blockId === selectedBlockId);
                      setEditFormData(prev => ({
                        ...prev,
                        blockId: selectedBlockId,
                        blockName: block ? block.blockName : ''
                      }));
                    }}
                    required
                    fullWidth
                    margin="normal"
                  >
                    <MenuItem value="">Select Block</MenuItem>
                    {getBlocksForBuilding(editFormData.buildingId).map(block => (
                      <MenuItem key={block.blockId} value={block.blockId}>
                        {block.blockName || block.blockId}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  select
                  SelectProps={{ native: true }}
                  label="Flat Type"
                  value={editFormData.flatType}
                  onChange={e => setEditFormData({ ...editFormData, flatType: e.target.value })}
                  required
                  fullWidth
                >
                  <option value="">Select Flat Type</option>
                  <option value="1">1BHK</option>
                  <option value="2">2BHK</option>
                  <option value="3">3BHK</option>
                  <option value="4">4BHK</option>
                  <option value="5">5BHK</option>
                </TextField>
                <TextField
                  label="Floor Number"
                  type="number"
                  value={editFormData.floorNumber}
                  onChange={e => setEditFormData({ ...editFormData, floorNumber: e.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  label="Area (sq ft)"
                  type="number"
                  value={editFormData.area}
                  onChange={e => setEditFormData({ ...editFormData, area: e.target.value })}
                  required
                  fullWidth
                />
                <TextField
                  label="Monthly Maintenance"
                  type="number"
                  value={editFormData.monthlyMaintenance}
                  onChange={e => setEditFormData({ ...editFormData, monthlyMaintenance: e.target.value })}
                  required
                  fullWidth
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            position: isMobile ? 'sticky' : 'static', 
            bottom: 0, 
            bgcolor: 'background.paper', 
            p: 2,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0
          }}>
            <Button 
              onClick={handleEditClose}
              fullWidth={isMobile}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={updateMutation.isLoading}
              fullWidth={isMobile}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              {updateMutation.isLoading ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Flats;